import React, { useState, useEffect, useCallback } from "react";
import { Users, Search, ShieldOff, ShieldCheck, Loader2, RefreshCw, UserCheck, UserX } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, suspended: 0 });
  const token = localStorage.getItem("ka_admin_token");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/users/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const [usersData, statsData] = await Promise.all([usersRes.json(), statsRes.json()]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      if (statsData && !statsData.error) setStats(statsData);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggle = async (user) => {
    const isSuspended = user.suspended === 1;
    const endpoint = isSuspended ? "activate" : "suspend";
    const label = isSuspended ? "activated" : "suspended";
    setActionLoading(user.id);
    try {
      const res = await fetch(`${API_URL}/users/${user.id}/${endpoint}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, suspended: isSuspended ? 0 : 1 } : u));
      setStats(prev => ({
        ...prev,
        active: prev.active + (isSuspended ? 1 : -1),
        suspended: prev.suspended + (isSuspended ? -1 : 1)
      }));
      toast.success(`${user.username} has been ${label}`, {
        description: isSuspended
          ? "User can now log in again."
          : "User will see a suspension message on login."
      });
    } catch {
      toast.error("Action failed", { description: "Please try again." });
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = users.filter(u =>
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold flex items-center gap-2 text-white">
          <Users className="w-5 h-5 text-[#8B0000]" />
          User Management
        </h3>
        <Button variant="ghost" onClick={fetchUsers} className="text-gray-400 hover:text-white" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Users", value: stats.total, icon: Users, color: "text-blue-400" },
          { label: "Active", value: stats.active, icon: UserCheck, color: "text-green-400" },
          { label: "Suspended", value: stats.suspended, icon: UserX, color: "text-red-400" },
        ].map((s) => (
          <Card key={s.label} className="bg-[#1a1a1a]/80 border-[#8B0000]/30 relative overflow-hidden group">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-white uppercase tracking-wider">{s.label}</CardTitle>
              <s.icon className={`w-5 h-5 ${s.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{s.value}</div>
            </CardContent>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-[#8B0000]/20 group-hover:bg-[#8B0000]/50 transition-all duration-300" />
          </Card>
        ))}
      </div>

      {/* Search + table */}
      <Card className="bg-[#1a1a1a]/80 border-[#8B0000]/30 overflow-hidden">
        <CardHeader className="bg-black/20 border-b border-[#8B0000]/10">
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 text-gray-500 shrink-0" />
            <Input
              placeholder="Search by username or email..."
              className="bg-transparent border-none text-white placeholder-gray-600 focus-visible:ring-0 p-0 h-auto"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#8B0000] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-600">
              {search ? "No users match your search." : "No registered users yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#8B0000]/10 text-xs text-gray-500 uppercase tracking-wider">
                    <th className="text-left px-6 py-3">User</th>
                    <th className="text-left px-6 py-3">Email</th>
                    <th className="text-left px-6 py-3">Joined</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-right px-6 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#8B0000]/10">
                  {filtered.map((user) => {
                    const isSuspended = user.suspended === 1;
                    return (
                      <tr key={user.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#8B0000]/20 border border-[#8B0000]/40 flex items-center justify-center text-sm font-bold text-white shrink-0">
                              {user.username?.[0]?.toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-white">{user.username}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">{user.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                        <td className="px-6 py-4">
                          {isSuspended ? (
                            <Badge className="bg-red-900/30 text-red-400 border border-red-500/30 text-xs">Suspended</Badge>
                          ) : (
                            <Badge className="bg-green-900/30 text-green-400 border border-green-500/30 text-xs">Active</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggle(user)}
                            disabled={actionLoading === user.id}
                            className={isSuspended
                              ? "text-green-400 hover:text-green-300 hover:bg-green-900/20"
                              : "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            }
                          >
                            {actionLoading === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isSuspended ? (
                              <><ShieldCheck className="w-4 h-4 mr-1" /> Activate</>
                            ) : (
                              <><ShieldOff className="w-4 h-4 mr-1" /> Suspend</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
